import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, ShoppingCart, HandCoins, Bell, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/UI";
import { useWidgetOrder, WidgetWrapper, WidgetEditButton } from "@/components/WidgetReorder";
import { PreviewCard } from "@/components/dashboard/PreviewCard";
import { FxConverterCard } from "@/components/dashboard/FxConverterCard";

const ORDER = ["alerts", "stock", "purchasing", "collections", "fx"];
const SIZES = { alerts: "FULL", stock: "M", purchasing: "M", collections: "FULL", fx: "FULL" };
const TITLES = { alerts: "Uyarılar", stock: "Stok Özeti", purchasing: "Açık Satın Almalar", collections: "Tahsilat / Ödeme", fx: "Kur Dönüştürücü" };

const fmtQ = (v) => Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const fmtTL = (v) => `${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
const fmtD = (d) => (d ? d.slice(0, 10).split("-").reverse().join(".") : "—");

function StockName({ r }) {
  return (
    <span className={`inline-flex items-center gap-1 ${r.is_critical ? "text-rose-700 font-semibold" : ""}`} data-testid={`dash-stock-${r.product_id}`}>
      {r.is_critical && <AlertTriangle size={11} />} {r.name}
    </span>
  );
}

function AlertsCard({ rows }) {
  return (
    <Card className="p-4" data-testid="dash-alerts">
      <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-800 mb-3"><Bell size={14} className="text-indigo-600" /> Uyarılar</h3>
      {rows.length === 0 ? <div className="text-xs text-slate-400 py-3 text-center">Uyarı yok</div> : (
        <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
          {rows.map((a) => (
            <li key={a.id} className="py-1.5 text-xs flex gap-3 items-center" data-testid={`dash-alert-${a.kind}`}>
              <span className="w-32 shrink-0 font-semibold text-slate-600">{a.title}</span>
              <Link to={a.to} className="text-slate-700 hover:text-indigo-700 truncate">{a.text}</Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CollectionsWidget({ c }) {
  const cust = [{ label: "Müşteri", render: (r) => r.customer_name }, { label: "Vade", render: (r) => fmtD(r.due_date) },
    { label: "Kalan", render: (r) => fmtTL(r.remaining) }];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <PreviewCard title="Geciken Tahsilatlar" icon={HandCoins} to="/finance?tab=balance" testid="dash-overdue-collections" rows={c.overdue} columns={cust} />
      <PreviewCard title="Yaklaşan Tahsilatlar" icon={HandCoins} to="/finance?tab=balance" testid="dash-upcoming-collections" rows={c.upcoming} columns={cust} />
      <PreviewCard title="Yaklaşan Tedarikçi Ödemeleri" icon={HandCoins} to="/finance?tab=supplier_balance" testid="dash-supplier-payments"
        rows={c.supplier_upcoming} columns={[{ label: "Tedarikçi", render: (r) => r.supplier_name }, { label: "Vade", render: (r) => fmtD(r.due_date) },
          { label: "Kalan", render: (r) => fmtTL(r.remaining) }]} />
    </div>
  );
}

export function CustomerErpWidgets() {
  const [d, setD] = useState(null);
  const { order, hidden, sizes, editMode, setEditMode, move, toggleHidden, cycleSize, resetOrder, reorderTo } = useWidgetOrder(ORDER, SIZES, "admin");
  const [dragKey, setDragKey] = useState(null);
  useEffect(() => { api.get("/dashboard/customererp-summary").then(setD).catch(() => setD({ sections: [], alerts: [] })); }, []);
  if (!d) return null;
  const has = (s) => d.sections.includes(s);
  const widgets = {
    alerts: <AlertsCard rows={d.alerts || []} />,
    stock: has("stock") && <PreviewCard title="Stok Özeti" icon={Boxes} to="/stock" testid="dash-stock" rows={d.stock}
      columns={[{ label: "Ürün", render: (r) => <StockName r={r} /> }, { label: "Kalan Stok", render: (r) => fmtQ(r.available) }]} />,
    purchasing: has("purchasing") && <PreviewCard title="Açık Satın Almalar" icon={ShoppingCart} to="/purchasing" testid="dash-purchasing" rows={d.purchasing}
      columns={[{ label: "Tedarikçi", key: "supplier" }, { label: "Ürün", key: "product_name" },
        { label: "Miktar", render: (r) => `${fmtQ(r.qty)} ${r.unit}` }, { label: "Beklenen", render: (r) => fmtD(r.expected_delivery) }]} />,
    collections: has("collections") && <CollectionsWidget c={d.collections} />,
    fx: <FxConverterCard />,
  };
  return (
    <div className="mb-5" data-testid="customererp-widgets">
      <div className="flex justify-end mb-2"><WidgetEditButton editMode={editMode} setEditMode={setEditMode} resetOrder={resetOrder} /></div>
      <div className="grid grid-cols-4 gap-4">
        {order.map((k, idx) => widgets[k] ? (
          <WidgetWrapper key={k} id={k} title={TITLES[k]} editMode={editMode} isFirst={idx === 0} isLast={idx === order.length - 1}
            isHidden={hidden.has(k)} size={sizes[k] || "FULL"} onMove={move} onToggleHide={toggleHidden} onCycleSize={cycleSize}
            onDragStart={() => setDragKey(k)} onDragOver={(e) => e.preventDefault()} onDrop={() => { reorderTo(dragKey, k); setDragKey(null); }}>
            {widgets[k]}
          </WidgetWrapper>
        ) : null)}
      </div>
    </div>
  );
}
